/**
 * Переключаемая подложка карты (`agroai-gpo.7.10`).
 *
 * По умолчанию подложки НЕТ: карта работает офлайн, не тащит чужих лицензий и
 * не отдаёт третьей стороне, куда смотрит пользователь. Включить её можно
 * переключателем на полотне или настройкой сборки `VITE_MAP_BASEMAP`.
 *
 * Источник один — EOX (Sentinel-2 cloudless 2025, CC BY 4.0) растровыми
 * тайлами: снимок и, для режима «подписи», растровый слой подписей
 * `overlay_bright` того же провайдера. Векторный стиль OpenFreeMap из
 * прототипа НЕ подключается: он требует глифов и спрайтов, то есть шрифтов из
 * сети, а полотно канона принципиально без символьных слоёв.
 *
 * Модуль чистый: здесь конфигурация и автомат состояния, а maplibre живёт в
 * `MapCanvas`. Автомат тестируется без WebGL.
 */

import { defineMessages } from '../../i18n/define';
import { tr } from '../../i18n/runtime';

const m = defineMessages({
  none: {
    id: 'datamap.basemap.none',
    defaultMessage: 'Без подложки',
    description:
      'Переключатель подложки на полотне карты (aria-label и подсказка): снимок выключен, видны только контуры; коротко',
  },
  satellite: {
    id: 'datamap.basemap.satellite',
    defaultMessage: 'Спутник',
    description: 'Переключатель подложки на полотне карты (aria-label и подсказка): спутниковый снимок под контурами; одно слово',
  },
  labels: {
    id: 'datamap.basemap.labels',
    defaultMessage: 'Спутник и подписи',
    description:
      'Переключатель подложки на полотне карты (aria-label и подсказка): спутниковый снимок плюс подписи населённых пунктов; коротко',
  },
  attrSatellite: {
    id: 'datamap.basemap.attributionSatellite',
    defaultMessage:
      'Sentinel-2 cloudless 2025 © EOX IT Services GmbH (CC BY 4.0), содержит изменённые данные Copernicus Sentinel 2025',
    description:
      'Строка авторства внизу карты со спутниковой подложкой (требование лицензии CC BY 4.0); названия, © и лицензию не менять, переводится только «содержит изменённые данные»',
  },
  attrLabels: {
    id: 'datamap.basemap.attributionLabels',
    defaultMessage:
      'Sentinel-2 cloudless 2025 и подписи © EOX IT Services GmbH (CC BY 4.0); данные подписей © участники OpenStreetMap',
    description:
      'Строка авторства внизу карты со спутниковой подложкой и подписями (лицензия CC BY 4.0); названия, © и лицензию не менять, переводятся только связки «и подписи», «данные подписей», «участники»',
  },
  offline: {
    id: 'datamap.basemap.offline',
    defaultMessage: 'нет сети',
    description:
      'Причина в сообщении «Подложка недоступна: …»: у устройства нет сети; строчная, без точки',
  },
  noReply: {
    id: 'datamap.basemap.noReply',
    defaultMessage: 'провайдер снимков не ответил',
    description:
      'Причина в сообщении «Подложка недоступна: …»: сервер снимков не ответил; строчная, без точки',
  },
});

/** Причины недоступности подложки — словами, а не текстом ошибки загрузчика. */
export const basemapOfflineReason = (): string => tr(m.offline);
export const basemapNoReplyReason = (): string => tr(m.noReply);

export type BasemapId = 'none' | 'satellite' | 'labels';

export const BASEMAP_IDS: readonly BasemapId[] = ['none', 'satellite', 'labels'];

export const basemapLabel = (id: BasemapId): string => tr(m[id]);

/** Источники и слои подложки — имена, по которым полотно ловит их ошибки. */
export const BASEMAP_SOURCE_IMAGERY = 'basemap-imagery';
export const BASEMAP_SOURCE_LABELS = 'basemap-labels';
export const BASEMAP_LAYER_IMAGERY = 'basemap-imagery';
export const BASEMAP_LAYER_LABELS = 'basemap-labels';
/**
 * Полупрозрачная маска цвета холста над снимком. Палитра данных (ADR-0009)
 * проверена валидатором на ровном холсте; пёстрый снимок под заливкой без
 * маски съедал бы ступени. Маска возвращает фон к проверенному, оставляя
 * снимок читаемым.
 *
 * С agroai-0yt.2 фигуру от фона отделяет маска ВНЕ района (MapCanvas,
 * `district-mask`), поэтому общая вуаль снижена с 0,35 до 0,1: внутри района
 * снимок больше не тонет вместе с соседями.
 */
export const BASEMAP_LAYER_MASK = 'basemap-mask';
export const BASEMAP_MASK_OPACITY = 0.1;

export const BASEMAP_SOURCES = new Set([BASEMAP_SOURCE_IMAGERY, BASEMAP_SOURCE_LABELS]);

const EOX = 'https://tiles.maps.eox.at/wmts/1.0.0';

export const IMAGERY_TILES = `${EOX}/s2cloudless-2025_3857/default/g/{z}/{y}/{x}.jpg`;
export const LABELS_TILES = `${EOX}/overlay_bright_3857/default/g/{z}/{y}/{x}.png`;

/** Максимальный зум провайдера: дальше тайлы растягиваются, а не запрашиваются. */
export const IMAGERY_MAXZOOM = 15;
export const LABELS_MAXZOOM = 16;

/** Строка атрибуции: лицензия CC BY 4.0 требует назвать источник на полотне. */
export const basemapAttribution = (id: Exclude<BasemapId, 'none'>): string =>
  tr(id === 'satellite' ? m.attrSatellite : m.attrLabels);

/**
 * Выбор по умолчанию из настройки сборки. Неизвестное значение — «без
 * подложки»: опечатка в окружении не должна молча отправлять запросы наружу.
 */
export function parseBasemap(raw: string | null | undefined): BasemapId {
  const value = (raw ?? '').trim().toLowerCase();
  return (BASEMAP_IDS as readonly string[]).includes(value) ? (value as BasemapId) : 'none';
}

export function defaultBasemap(): BasemapId {
  return parseBasemap(import.meta.env.VITE_MAP_BASEMAP as string | undefined);
}

/**
 * Состояние подложки на полотне.
 *
 * `off` — выключена; `loading` — выбрана, тайлы ещё не пришли; `shown` —
 * хотя бы один тайл нарисован; `unavailable` — сеть или провайдер отказали:
 * фон остаётся холстом, и полотно говорит «подложка недоступна». Отказ
 * подложки — НЕ отказ карты: контуры продолжают рисоваться.
 */
export type BasemapStatus =
  | { kind: 'off' }
  | { kind: 'loading'; id: Exclude<BasemapId, 'none'> }
  | { kind: 'shown'; id: Exclude<BasemapId, 'none'> }
  | { kind: 'unavailable'; id: Exclude<BasemapId, 'none'>; reason: string };

export type BasemapEvent =
  | { type: 'select'; id: BasemapId; online: boolean }
  | { type: 'tile-loaded' }
  | { type: 'tile-error'; message: string };

export const BASEMAP_OFF: BasemapStatus = { kind: 'off' };

export function basemapReducer(state: BasemapStatus, event: BasemapEvent): BasemapStatus {
  switch (event.type) {
    case 'select':
      if (event.id === 'none') return BASEMAP_OFF;
      if (!event.online) {
        return { kind: 'unavailable', id: event.id, reason: basemapOfflineReason() };
      }
      return { kind: 'loading', id: event.id };
    case 'tile-loaded':
      return state.kind === 'loading' ? { kind: 'shown', id: state.id } : state;
    case 'tile-error':
      // Ошибка после первого удачного тайла тоже означает дыры в снимке:
      // честнее снять подложку целиком, чем показывать лоскутное одеяло.
      if (state.kind === 'loading' || state.kind === 'shown') {
        return {
          kind: 'unavailable',
          id: state.id,
          reason: event.message || basemapNoReplyReason(),
        };
      }
      return state;
  }
}

/** Рисуется ли подложка на полотне при этом состоянии. */
export function basemapVisible(status: BasemapStatus): boolean {
  return status.kind === 'loading' || status.kind === 'shown';
}

/** Какой выбор показывать переключателю. */
export function selectedBasemap(status: BasemapStatus): BasemapId {
  return status.kind === 'off' ? 'none' : status.id;
}
